import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/SEOHead";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  CalendarIcon, Phone, Mail, MessageCircle, Gift, Sparkles,
  Briefcase, ArrowRight,
  ClipboardList, Palette, Truck,
} from "lucide-react";
import { format } from "date-fns";

const HERO_IMG = "https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/i9am5wl3_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2003_24_02%20PM.png&w=1800&q=85&output=webp";
const HAMPER_DETAIL_IMG = "https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/q6g3t1iy_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2006_20_44%20PM.png&w=1400&h=1400&fit=cover&q=82&output=webp";

const USE_CASES = [
  { icon: Sparkles, title: "Festive Hampers", sub: "Diwali · Christmas · Eid · New Year",
    desc: "Celebrate the big days with personalised, beautifully boxed treats." },
  { icon: Briefcase, title: "Corporate Gifting", sub: "Clients · Employees · Events · Welcome Kits",
    desc: "Branded hampers that say thank-you, welcome aboard, and well done — beautifully." },
  { icon: Gift, title: "Personal Gifting", sub: "Birthdays · Anniversaries · House-warmings",
    desc: "Curated, beautifully presented hampers for the people you love." },
];

const STEPS = [
  { icon: ClipboardList, title: "Step 1 · Consult",
    desc: "Tell us your occasion, scale and budget. We'll suggest curated options." },
  { icon: Palette, title: "Step 2 · Design",
    desc: "Pick our ready hampers or co-design your own — sleeves, ribbons, branded cards." },
  { icon: Truck, title: "Step 3 · Deliver",
    desc: "We bake fresh, pack with care, and deliver across Mumbai (pan-India on request)." },
];

const FAQS = [
  { q: "What is the minimum order quantity?",
    a: "For fully customised corporate hampers we suggest a minimum of 25 boxes. For smaller orders, you can pick our standard hampers — built fresh on demand with a 3-day lead time." },
  { q: "What's the lead time?",
    a: "Standard hampers: 3 days. Customised hampers: 5–7 working days. During Diwali/Christmas season, please add a couple of extra days." },
  { q: "Can I personalise the packaging?",
    a: "Yes — we can add custom sleeves, hand-written tags, branded ribbon, foiled logos and printed thank-you cards. Send us your brand assets in the inquiry below." },
  { q: "Do you offer bulk discounts?",
    a: "Yes — pricing scales with quantity and final billing value. Our team will share a tailored quote within 24 hours of receiving your inquiry." },
  { q: "Do you deliver outside Mumbai?",
    a: "Mumbai delivery is same-day. For other cities we partner with reliable courier services (lead time + shipping charges apply)." },
  { q: "What's typically inside a Cocoamaya hamper?",
    a: "Our boxes can include macarons, cakes, brownies, artisan cookies, gianduja chocolates, savoury bakes and curated add-ons (artisanal honey, jams, teas). All hand-crafted in our Mumbai patisserie." },
];

const OCCASIONS = ["Corporate", "Festive (Diwali / Eid / Christmas)", "Wedding", "Birthday", "Anniversary", "House-warming", "Other"];
const QUANTITIES = ["Under 25", "25–50", "50–100", "100–250", "250–500", "500+"];

export default function GiftHampers() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    occasion: OCCASIONS[0],
    quantity_range: QUANTITIES[0],
    delivery_location: "",
    message: "",
  });
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please pick a tentative gifting date"); return; }
    if (!/^[0-9]{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/gift-hampers/inquiry", {
        ...form,
        delivery_date: date.toISOString(),
      });
      setDone(r.data);
      toast.success("Inquiry received — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="hamper-success">
        <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Inquiry received</p>
        <h1 className="font-display text-5xl mb-6">Thanks, {form.first_name}!</h1>
        <p className="text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          Our gifting concierge will reach out to you on <strong>{form.email}</strong> within 24 hours
          with a tailored proposal. For anything urgent, WhatsApp us at +91 98193 00124.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link to="/" className="btn-secondary">Back home</Link>
          <a
            href="https://wa.me/919819300124?text=Hi%2C%20I%20just%20submitted%20a%20Cocoamaya%20hamper%20inquiry%20and%20wanted%20to%20follow%20up"
            target="_blank" rel="noreferrer" className="btn-primary"
          >
            <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp now
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="gift-hampers-page">
      <SEOHead
        title="Corporate & Festive Gift Hampers in Mumbai | Cocoamaya Patisserie"
        description="Curated corporate and festive gift hampers from Cocoamaya Patisserie — hand-crafted patisserie favourites, personalised packaging, pan-India delivery."
        keywords="gift hampers mumbai, corporate gift hampers, diwali hampers mumbai, festive gift boxes, luxury gift hampers, hamper delivery india, cocoamaya, patisserie gift hampers"
        url="https://cocoamayapatisserie.com/gift-hampers"
        image="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/i9am5wl3_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2003_24_02%20PM.png&w=1200&h=630&fit=cover&q=82&output=jpg"
      />
      {/* Hero */}
      <section className="px-4 md:px-8 pt-6">
        <div
          className="max-w-7xl mx-auto rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-2 items-stretch"
          style={{ background: "var(--bg-card)" }}
        >
          <div className="p-8 md:p-14 lg:p-16 flex flex-col justify-center fade-up">
            <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Cocoamaya Gifting</p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              style={{ color: "var(--ink)" }}
            >
              Curated hampers,<br/>
              <span className="italic">unforgettably yours.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-md" style={{ color: "var(--ink-soft)" }}>
              From Diwali surprises to corporate thank-yous — we craft beautifully boxed
              hampers with our patisserie's finest, personalised to your brand.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="#hamper-form" className="btn-primary" data-testid="hero-enquire-btn">
                Plan my hamper <ArrowRight size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://wa.me/919819300124?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20Cocoamaya%20Gift%20Hampers"
                target="_blank" rel="noreferrer"
                className="btn-secondary inline-flex"
                data-testid="hero-wa-btn"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp us
              </a>
            </div>
          </div>
          <div className="relative min-h-[280px] md:min-h-[480px]">
            <img
              src={HERO_IMG}
              alt="Cocoamaya gift hamper"
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 text-center">
          {[
            { num: "Made fresh", label: "Hand-crafted in Mumbai" },
            { num: "100+ corporate", label: "Hampers delivered every month" },
            { num: "Pan-India", label: "Delivery on request" },
          ].map((s) => (
            <div key={s.label} className="p-6">
              <p className="font-display text-3xl md:text-4xl italic" style={{ color: "var(--ink)" }}>{s.num}</p>
              <p className="mt-2 text-sm md:text-base" style={{ color: "var(--ink-soft)" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="text-center mb-12 md:mb-16">
          <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Solutions for every occasion</p>
          <h2 className="font-display text-4xl md:text-5xl italic">We've gifted them all.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {USE_CASES.map((u) => {
            const Icon = u.icon;
            return (
              <div
                key={u.title}
                className="rounded-2xl p-7 transition-transform hover:-translate-y-1"
                style={{ background: "var(--bg-card)" }}
                data-testid={`usecase-${u.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <Icon size={28} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
                <h3 className="font-display text-2xl italic mt-4">{u.title}</h3>
                <p className="eyebrow text-[11px] mt-2" style={{ color: "var(--ink-soft)" }}>{u.sub}</p>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink)" }}>{u.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What's inside */}
      <section style={{ background: "var(--bg-warm)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden">
            <img src={HAMPER_DETAIL_IMG} alt="Cocoamaya hamper contents" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>What's Inside</p>
            <h2 className="font-display text-4xl md:text-5xl italic mb-6" style={{ color: "var(--ink)" }}>
              Hand-crafted goodness, <br/>beautifully boxed.
            </h2>
            <p className="text-lg leading-relaxed mb-8" style={{ color: "var(--ink-soft)" }}>
              Pick a curated hamper or build your own. Every box is assembled to order, sealed
              fresh and delivered with care.
            </p>
            <ul className="space-y-3 text-base" style={{ color: "var(--ink)" }}>
              {[
                "Macarons, cakes, brownies & gianduja chocolates",
                "Artisan cookies, sourdough crisps & savoury bakes",
                "Curated add-ons: honey, jams, single-origin teas, dried fruit",
                "Personalised sleeves, branded ribbons, hand-written tags",
                "Print options: gold foiling, UV lamination, embossed monograms",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="font-display text-xl" style={{ color: "var(--accent)" }}>·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <a href="#hamper-form" className="btn-primary mt-10 inline-flex" data-testid="inside-cta-btn">
              Talk to our gifting team <ArrowRight size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* 3-step process */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-12 md:mb-16">
          <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>How it works</p>
          <h2 className="font-display text-4xl md:text-5xl italic">From brief to box, in three steps.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="rounded-2xl p-8 text-center"
                style={{ background: "var(--bg-card)" }}
                data-testid={`step-${i + 1}`}
              >
                <div
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ background: "var(--bg-warm)" }}
                >
                  <Icon size={26} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
                </div>
                <h3 className="font-display text-2xl italic mt-5">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Inquiry form */}
      <section id="hamper-form" style={{ background: "var(--bg-card)" }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-10 md:mb-12">
            <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Help us plan your hamper</p>
            <h2 className="font-display text-4xl md:text-5xl italic">Tell us about your gifting.</h2>
            <p className="mt-5 text-base md:text-lg" style={{ color: "var(--ink-soft)" }}>
              Or reach us directly via{" "}
              <a href="tel:+919819300124" className="underline" style={{ color: "var(--accent)" }}>Call</a>{" · "}
              <a href="https://wa.me/919819300124" target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--accent)" }}>WhatsApp</a>{" · "}
              <a href="mailto:hello@cocoamayapatisserie.com" className="underline" style={{ color: "var(--accent)" }}>Email</a>
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="hamper-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="input-field" placeholder="First name *" required
                value={form.first_name} onChange={(e) => onChange("first_name", e.target.value)}
                data-testid="hf-first-name"
              />
              <input
                className="input-field" placeholder="Last name *" required
                value={form.last_name} onChange={(e) => onChange("last_name", e.target.value)}
                data-testid="hf-last-name"
              />
              <input
                type="email" className="input-field" placeholder="Email *" required
                value={form.email} onChange={(e) => onChange("email", e.target.value)}
                data-testid="hf-email"
              />
              <div className="flex gap-2">
                <span
                  className="input-field flex items-center justify-center select-none"
                  style={{ width: "70px", padding: "0.85rem 0", flex: "0 0 70px" }}
                >+91</span>
                <input
                  type="tel" className="input-field flex-1" placeholder="Phone *" required
                  value={form.phone}
                  onChange={(e) => onChange("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                  data-testid="hf-phone"
                />
              </div>

              <select
                className="input-field"
                value={form.occasion}
                onChange={(e) => onChange("occasion", e.target.value)}
                data-testid="hf-occasion"
              >
                {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <select
                className="input-field"
                value={form.quantity_range}
                onChange={(e) => onChange("quantity_range", e.target.value)}
                data-testid="hf-quantity"
              >
                {QUANTITIES.map((q) => <option key={q} value={q}>{q} hampers</option>)}
              </select>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="input-field flex items-center justify-between text-left"
                    data-testid="hf-date-trigger"
                  >
                    <span style={{ color: date ? "var(--ink)" : "var(--ink-soft)" }}>
                      {date ? format(date, "PPP") : "Date of gifting *"}
                    </span>
                    <CalendarIcon size={16} strokeWidth={1.5} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <input
                className="input-field" placeholder="Delivery location (city / pin code) *" required
                value={form.delivery_location}
                onChange={(e) => onChange("delivery_location", e.target.value)}
                data-testid="hf-location"
              />
            </div>

            <textarea
              className="input-field"
              placeholder="Anything else? (budget, branding requirements, dietary preferences…)"
              rows={4}
              value={form.message}
              onChange={(e) => onChange("message", e.target.value)}
              data-testid="hf-message"
            />

            <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
              We use your contact info only to respond about your gifting inquiry.
            </p>

            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={submitting}
              data-testid="hf-submit"
            >
              {submitting ? "Sending…" : "Send inquiry"} <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-10 md:mb-14">
          <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>Frequently Asked</p>
          <h2 className="font-display text-4xl md:text-5xl italic">The fine print, simply.</h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="hamper-faq">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b" style={{ borderColor: "var(--line)" }}>
              <AccordionTrigger className="text-left font-display text-lg md:text-xl italic py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed pb-5" style={{ color: "var(--ink-soft)" }}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Closing CTA */}
      <section style={{ background: "var(--bg-warm)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <h2 className="font-display text-4xl md:text-5xl italic" style={{ color: "var(--ink)" }}>
            Ready to gift something memorable?
          </h2>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            Send us an inquiry above, or reach out on WhatsApp / phone / email — we typically respond within 24 hours.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-3 justify-center">
            <a href="#hamper-form" className="btn-primary" data-testid="closing-form-cta">
              Plan my hamper
            </a>
            <a
              href="https://wa.me/919819300124?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20Cocoamaya%20Gift%20Hampers"
              target="_blank" rel="noreferrer"
              className="btn-secondary inline-flex"
            >
              <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp now
            </a>
            <a href="tel:+919819300124" className="btn-secondary inline-flex">
              <Phone size={16} strokeWidth={1.5} /> Call us
            </a>
            <a href="mailto:hello@cocoamayapatisserie.com" className="btn-secondary inline-flex">
              <Mail size={16} strokeWidth={1.5} /> Email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
